
import {Contact} from "@/models/base";
import {ContactAddress} from "@/models/base";
import {Sale} from "@/models/base";
import {SaleEditState} from "@/utils/sale-edit/sale-edit-state";
import Vue from "vue";
import {ISelectItem} from "winfakt-vue-components";
import AppSelectJournal from "@/components/SelectJournal.vue";
import {AuthService} from "@/services/auth-service";
import {MODULE} from "@/config/modules";
import AppContactSearch from "@/components/contacts/ContactSearchWithBrowseAndCreate.vue";
import {CustomerService} from "@/services";
import AppCustomLanguageSelect from "@/components/CustomLanguageSelect.vue";
import AppDefaultSaleCreatorSelect from "./DefaultSaleCreatorSelect.vue";

export default Vue.extend({
	data() {
		const savedState = localStorage.getItem("saleEditSaveModalToggleState");
		const toggleState = savedState ? JSON.parse(savedState) : { addresses: false, discounts: false, other: false };
		return {
			deliveryContactID: null,
			deliveryAddresses: null as null | ISelectItem<number | null>[],
			alternateDeliveryAddressGroupId: 0,
			toggleAddresses: toggleState.addresses,
			toggleDiscounts: toggleState.discounts,
			toggleOther: toggleState.other
		};
	},
	props: {
		state: {
			type: Object as () => SaleEditState,
			required: true
		}
	},
	computed: {
		isAllowedMultiLanguages(){
			return AuthService.hasModule(MODULE.MULTITALEN);
		},
		hasRepresentativeModule():boolean {
			return AuthService.hasModule(MODULE.WERKEN_MET_VERKOPERS);
		},
		hasDepositModule():boolean {
			return AuthService.hasModule(MODULE.VOORSCHOTVERWERKING);
		},
		sale(): Sale {
			return this.state.sale;
		},
		contact(): Contact {
			if (!this.state.sale.Contact) {
				throw new Error("NO CONTACT");
			}
			return this.state.sale.Contact;
		},
		selectAddresses(): ISelectItem<number>[] {
			return this.contact.Addresses.map(a => {
				return {
					text: a.toFullAddressString(),
					value: a.ID
				};
			});
		},
		selectDeliveryAddres(): ISelectItem<number | null>[] {
			return [
				{
					text: this.$ct("none").toString(),
					value: null
				},
				...this.selectAddresses
			];
		},
		selectedInvoiceAddress(): ContactAddress | undefined {
			return this.contact.Addresses.find(a => a.ID == this.sale.InvoiceAddressID);
		},
		locale(): string {
			return this.$i18n.locale;
		},
		hasJournalModule(): boolean {
			return AuthService.hasModule(MODULE.TOT_99_JOURNALEN);
		},
		reminders(): ISelectItem<number>[] {
			return Object.keys(this.$ct("reminders")).map((k, v) => {
				return {value: parseInt(k), text: this.$ct(`reminders.${k}`).toString()};
			});
		},
		frequencyDocumentPaymentDays: {
			get():number{
				return Math.floor((this.sale.ExpirationDate.getTime() - this.sale.InvoiceDate.getTime()) / (1000 * 60 * 60 * 24));
			},
			set(value:number){
				let date = new Date(this.sale.InvoiceDate);
				date.setDate(date.getDate() + value);
				this.sale.ExpirationDate = date;
			}
		}
	},
	watch: {
		contact: {
			immediate: true,
			async handler(sale) {
				if (this.selectedInvoiceAddress && !this.sale.Lng)
					this.sale.Lng = this.selectedInvoiceAddress.Lng;
			}
		},
		"sale.JournalID": {
			async handler() {
				this.fetchFriendlyId();
			}
		}
	},
	methods: {
		setLng(lng:string){
			this.state.setLng(lng);
		},
		setPaymentBeforeDiscountPercent(value: number) {
			if (this.sale.PaymentBeforeDiscountPercent == 0.0) {
				this.sale.PaymentBeforeDiscountPercent = value;
				if (this.sale.Contact) {
					this.sale.PaymentBeforeDiscountDate = new Date(this.sale.InvoiceDate.toJSON());
					this.sale.PaymentBeforeDiscountDate.setDate(this.sale.PaymentBeforeDiscountDate.getDate() + this.sale.Contact.DiscountCashDays);
				}
			}
			this.sale.PaymentBeforeDiscountPercent = value;
			this.sale.calculateTotals();
		},
		setInvoiceDate(event: Date) {
			this.state.setInvoiceDate(event);
			this.setPaymentBeforeDiscountPercent(this.sale.PaymentBeforeDiscountPercent);
		},
		async fetchFriendlyId() {
			await this.state.loadNextFriendlyId();
		},
		setDeliveryAddresses() {

			this.deliveryAddresses = this.selectAddresses;

			let d = this.contact.Addresses.find(a => a.ID == this.sale.DeliveryAddressID);
			if (!d && this.sale.DeliveryAddress?.ContactID) {
				this.deliveryAddresses?.push(
					{text: this.sale.DeliveryAddress.toFullAddressString(), value: this.sale.DeliveryAddressID});
			}

			this.deliveryAddresses.push({
				text: this.$ct("none").toString(),
				value: null
			});

		},
		async fetchContact(contact: Contact) {
			let c = await CustomerService.getContact(contact.ID);
			this.deliveryAddresses = c.Addresses.map(
				(a) => {
					return {text: a.toFullAddressString(), value: a.ID};
				}
			);
			this.sale.DeliveryAddressID = c.MainAddressID;
		},
		setUseCreditRestrictionInsteadOfDiscountPercent(value:boolean){
			this.sale.UseCreditRestrictionInsteadOfDiscountPercent = value;
			this.sale.calculateTotals();
		},
		saveToggleState() {
			localStorage.setItem("saleEditSaveModalToggleState", JSON.stringify({
				addresses: this.toggleAddresses,
				discounts: this.toggleDiscounts,
				other: this.toggleOther
			}));
		},
	},
	async created() {
		this.setDeliveryAddresses();
		await this.fetchFriendlyId();
		if (this.sale.Contact) {
			this.alternateDeliveryAddressGroupId = this.sale.Contact.GroupID;
		}
	},
	components: {
		AppSelectJournal,
		AppContactSearch,
		AppCustomLanguageSelect,
		AppDefaultSaleCreatorSelect
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
